import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Backdrop,
  Box,
  CircularProgress,
  Container,
  Icon,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  Deployment,
  createDeployment,
} from "../../models/Deployment";
import { getCreds, PageContext } from "../../contexts/PageContext";
import PageHeader from "../../components/PageHeader";
import Minibar from "../../components/Minibar";
import { Save, Delete, AddCircle } from "@mui/icons-material";
import {
  deleteRecord,
  loadOrgModel,
  loadOrgRecords,
  saveRecord,
} from "../../api/connection";
import {
  showConfirmDialog,
  showInputDialog,
} from "../../components/UniversalDialog";
import { getCollectionName } from "../../models/ModelNavigator";
import { Robot, createRobot } from "../../models/Robot";
import NavigatableSelect from "../../components/NavigatableSelect";
import { createSiteAnchor, SiteAnchor } from "../../models/SiteAnchor";
import { FlexContainer } from "../../components/FlexContainer";
import { RobotDeploymentEdit } from "./RobotDeploymentEdit";
import { SignalingConnection } from "../../api/signaling/SignalingConnection";

export interface RobotStatusInfo {
  _id: string;
  active: number;
}

export const EditDeploymentPage = () => {
  const { id } = useParams<{ id: string }>();
  const { isLoggedIn, user, org } = useContext(PageContext);
  const navigate = useNavigate();
  const [deployment, setDeployment] = useState<Deployment>();
  const [mapIdToName, setMapIdToName] = useState<Record<string, string>>({});
  const [robotIdToName, setRobotIdToName] = useState<Record<string, string>>({});
  const [robotsChecked, setRobotsChecked] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [signaling, setSignaling] = useState<SignalingConnection>();
  const [statusQueryResults, setStatusQueryResults] = useState<RobotStatusInfo[]>([]);

  const [errors, setErrors] = useState({
    name: "",
    siteId: "",
  });

  useEffect(() => {
    let statusCheckerInterval: any = null;
    async function fetchDeployment(id: string) {
      try {
        const robotCollection = getCollectionName(createRobot());
        const robots = await loadOrgRecords<Robot>(robotCollection);
        const robotIdToName: Record<string, string> = {};
        robots.forEach((robot) => {
          robotIdToName[robot._id] = robot.name;
        });
        setRobotIdToName(robotIdToName);
        const sites = await loadOrgRecords<SiteAnchor>(
          getCollectionName(createSiteAnchor())
        );
        const mapIdToName: Record<string, string> = {};
        sites.forEach((map) => {
          mapIdToName[map._id] = map.id;
        });
        setMapIdToName(mapIdToName);
        if (id === "new") {
          setDeployment(createDeployment());
        } else {
          const deployment = createDeployment(id);
          await loadOrgModel(deployment);
          setDeployment(deployment);
          // setOriginalDeployment(deployment);
          const checked = deployment.robots.map((robot) => robot.robotId);
          setRobotsChecked(checked);
          const { org } = getCreds();
          const sc = new SignalingConnection({
            deviceId: org._id,
            token: org.token,
            group: "techmage-query",
          });
          const connected = await sc.connect();
          if (connected) {
            const results = (await sc.sendQuery("OrgRobotStates")) as any[];
            setSignaling(sc);
            setStatusQueryResults(results);
            statusCheckerInterval = setInterval(async () => {
              const results = (await sc.sendQuery("OrgRobotStates")) as any[];
              setStatusQueryResults(results);
            }, 15000);
          }
        }
      } catch (err) {
        setError("Failed to fetch deployment data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    if (id && user) {
      // console.log({ user });
      fetchDeployment(id);
    }
    return () => {
      if (statusCheckerInterval) {
        clearInterval(statusCheckerInterval);
      }
    }
  }, [id, user]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    updatedDeployment: Deployment
  ) => {
    const { name, value } = e.target;
    setDeployment({ ...updatedDeployment, [name]: value });
  };

  const handleChangeRobots = (
    robotId: string,
    updatedDeployment: Deployment
  ) => {
    let updatedRobots = [...updatedDeployment.robots];
    if (robotsChecked.includes(robotId)) {
      updatedRobots = updatedRobots.filter(
        (robot) => robot.robotId !== robotId
      );
    } else {
      updatedRobots.push({
        robotId,
        pose: {
          x: 0,
          y: 0,
          z: 30,
          w: 0,
        },
      });
    }
    setDeployment({ ...updatedDeployment, robots: updatedRobots });
    setRobotsChecked((prev) =>
      prev.includes(robotId)
        ? prev.filter((id) => id !== robotId)
        : [...prev, robotId]
    );
  };

  const saveDeployment = async (record: Deployment) => {
    setLoading(true);
    if (!validateFields()) {
      setLoading(false);
      return; // Stop if validation fails
    }

    try {
      console.log("Saving deployment:", record);
      const { org } = getCreds();
      record.orgId = org._id;
      const res = await saveRecord<Deployment>("deployments", record);
      navigate("/deployments");
    } catch (err) {
      console.error(
        `Failed to ${record._id === "new" ? "save" : "update"} deployment:`,
        err
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDeployment = async (record: Deployment) => {
    setLoading(true);
    try {
      const res = await deleteRecord<Deployment>("deployments", record);
      console.log("Delete Response:", res);
      navigate("/deployments");
    } catch (err) {
      console.error("Failed to delete deployment:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (deployment) {
      handleChange(e, deployment);
    }
  };

  const handleDeleteClick = async () => {
    const yes = await showConfirmDialog(
      "CONFIRM DELETION",
      "Are you sure you want to delete this deployment?"
    );
    if (yes && deployment) {
      await handleDeleteDeployment(deployment);
    }
  };

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  const validateFields = () => {
    const newErrors = {
      name: !deployment?.name ? "Name is required" : "",
      siteId: !deployment?.siteId ? "Synthetic scene ID is required" : "",
    };

    setErrors(newErrors);

    // If there are no errors, return true
    return Object.values(newErrors).every((error) => error === "");
  };

  const addRobot = async () => {
    const robotNames = Object.keys(robotIdToName).filter(
      (id) => !(deployment!.robots.some((robot) => robot.robotId === id))
    )
    .map(id => robotIdToName[id]);
    const selected = await showInputDialog(
      "Add Robot",
      "Select a robot to add to this deployment:",
      robotNames.join(",")
    );
    if (selected) {
      const robotId = Object.entries(robotIdToName).find(([id, name]) => name === selected)?.[0];
      if (robotId && deployment) {
        setDeployment({
          ...deployment,
          robots: [
            ...deployment.robots,
            {
              robotId,
              pose: {
                x: 0,
                y: 0,
                z: 30,
                w: 0,
              },
            },
          ],
        });
      }
    }
  };

  if (loading || !deployment) {
    return (
      <Backdrop open={true} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            backgroundColor: "#212121",
            color: "#ECECEC",
            minHeight: "100vh",
          }}
        >
          <Backdrop
            open={loading}
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          <PageHeader
            title={id === "new" ? "New Deployment" : "Edit Deployment Details"}
            rightComponent={[
              <Minibar
                minibarItems={
                  id === "new"
                    ? [
                        {
                          text: `Save New Deployment`,
                          icon: <Save />,
                          onClick: () =>
                            deployment && saveDeployment(deployment),
                        },
                      ]
                    : [
                        {
                          text: `Update Deployment`,
                          icon: <Save />,
                          onClick: () =>
                            deployment && saveDeployment(deployment),
                          disabled: loading,
                        },
                        {
                          text: "Delete Deployment",
                          icon: <Delete />,
                          onClick: () => handleDeleteClick(),
                          disabled: loading,
                        },
                      ]
                }
                barLength={3}
              />,
            ]}
          />

          <Container maxWidth="md">
            <Grid
              container
              sx={{ paddingTop: 3 }}
              spacing={3}
              justifyContent="center"
              alignItems="center"
            >
              <Grid size={12}>
                <Stack spacing={2}>
                  <FlexContainer>
                    <TextField
                      label="Name"
                      name="name"
                      value={deployment?.name || ""}
                      fullWidth
                      onChange={handleInputChange}
                      error={!!errors.name}
                      helperText={errors.name}
                    />
                    <NavigatableSelect
                      label="Site"
                      name="siteId"
                      value={deployment?.siteId || ""}
                      onChange={handleInputChange}
                      error={!!errors.siteId}
                      helperText={errors.siteId}
                      linkTo={`/sites/${deployment?.siteId}`}
                      tooltip="Go to site"
                      sx={{ width: "200px" }}
                      options={Object.entries(mapIdToName).map(
                        ([id, name]) => ({
                          value: id,
                          label: name,
                        })
                      )}
                      fullWidth
                    />
                  </FlexContainer>
                  <FlexContainer sx={{ paddingTop: 2 }}>
                    <Typography variant="h5">ROBOTS</Typography>
                    <IconButton color='secondary' size='large' onClick={addRobot}>
                      <AddCircle fontSize="large" />
                    </IconButton>
                  </FlexContainer>
                  {deployment.robots.map(r =>
                    <RobotDeploymentEdit
                      statusInfo={statusQueryResults.find(s => s._id === r.robotId)}
                      key={r.robotId}
                      deployment={deployment}
                      robotDeployment={r}
                      setDeployment={setDeployment}
                    />)}
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
    </>
  );
};
